import { useEffect, useImperativeHandle, forwardRef } from "react"
import { EditorState } from "lexical"
import { LexicalComposer } from "@lexical/react/LexicalComposer"
import { ContentEditable } from "@lexical/react/LexicalContentEditable"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"
import { ListPlugin } from "@lexical/react/LexicalListPlugin"
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin"
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin"
import { TRANSFORMERS } from "@lexical/markdown"
import { $getRoot, $createParagraphNode, $createTextNode, $getSelection } from "lexical"

import { HeadingNode, QuoteNode } from "@lexical/rich-text"
import { ListItemNode, ListNode } from "@lexical/list"
import { CodeHighlightNode, CodeNode } from "@lexical/code"
import { AutoLinkNode, LinkNode } from "@lexical/link"

import { Toolbar } from "./toolbar"
import { editorTheme } from "@/components/blocks/editor-00/theme"

const placeholder = "Digite seu conteúdo aqui..."

const editorConfig = {
  namespace: "complete-editor",
  theme: editorTheme,
  onError(error: Error) {
    throw error
  },
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    CodeHighlightNode,
    AutoLinkNode,
    LinkNode,
  ],
}

function MyOnChangePlugin({ onChange }: { onChange: (editorState: EditorState) => void }) {
  const [editor] = useLexicalComposerContext()
  
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      onChange(editorState)
    })
  }, [editor, onChange])

  return null
}

function UpdateContentPlugin({ content }: { content: string }) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    if (content && content.trim()) {
      editor.update(() => {
        const root = $getRoot()
        root.clear()
        
        // Limpar caracteres de escape e formatação JSON
        let cleanContent = content
        
        // Se o conteúdo parece ser JSON escapado, tentar fazer parse
        if (content.startsWith('"') && content.endsWith('"')) {
          try {
            cleanContent = JSON.parse(content)
          } catch (e) {
            // Se falhar, remover aspas e barras de escape manualmente
            cleanContent = content.slice(1, -1).replace(/\\n/g, '\n').replace(/\\"/g, '"')
          }
        }
        
        // Dividir o conteúdo em parágrafos
        const paragraphs = cleanContent.split(/\n\s*\n/)
        
        paragraphs.forEach((paragraphText) => {
          const trimmedText = paragraphText.trim()
          if (trimmedText) {
            const paragraph = $createParagraphNode()
            const textNode = $createTextNode(trimmedText)
            paragraph.append(textNode)
            root.append(paragraph)
          }
        })
      })
    }
  }, [content, editor])

  return null
}

interface CompleteEditorProps {
  initialValue?: string
  onChange?: (value: string) => void
  placeholder?: string
  content?: string // Novo prop para atualizar conteúdo externamente
}

export function CompleteEditor({ 
  initialValue,
  onChange,
  placeholder: customPlaceholder = placeholder,
  content 
}: CompleteEditorProps) {
  const handleChange = (editorState: EditorState) => {
    editorState.read(() => {
      // Extrair texto puro ao invés de JSON
      const root = $getRoot()
      const textContent = root.getTextContent()
      onChange?.(textContent)
    })
  }

  // Configuração do editor com estado inicial
  const initialConfig = {
    ...editorConfig,
    editorState: initialValue ? initialValue : undefined
  }

  return (
    <div className="relative rounded-md border border-input bg-background">
      <LexicalComposer initialConfig={initialConfig}>
        <Toolbar />
        <div className="relative lexical-editor-container">
          <style dangerouslySetInnerHTML={{
            __html: `
              .lexical-editor-container * {
                writing-mode: horizontal-tb !important;
                direction: ltr !important;
                text-orientation: mixed !important;
              }
              .lexical-editor-container [contenteditable] {
                writing-mode: horizontal-tb !important;
                direction: ltr !important;
              }
            `
          }} />
          <RichTextPlugin
            contentEditable={
              <ContentEditable 
                className="min-h-[200px] resize-none px-3 py-2 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                style={{ 
                  caretColor: "rgb(5, 5, 5)",
                  writingMode: "horizontal-tb",
                  direction: "ltr",
                  textAlign: "left"
                }}
              />
            }
            placeholder={
              <div className="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground">
                {customPlaceholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangePlugin onChange={handleChange} />
          <MyOnChangePlugin onChange={handleChange} />
          <UpdateContentPlugin content={content || ''} />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
        </div>
      </LexicalComposer>
    </div>
  )
}
